const RoutineBuilderManager = {
	config: {
		selectors: {
			root: '[data-routine-builder]',
			steps: '[data-routine-builder-steps]',
			card: '[data-routine-builder-card]',
			add: '[data-routine-builder-add]',
			stepper: '[data-routine-builder-stepper]',
			increase: '[data-routine-builder-increase]',
			decrease: '[data-routine-builder-decrease]',
			quantity: '[data-routine-builder-quantity-value]',
			summary: '[data-routine-builder-summary]',
			summaryTitle: '.mm-routine-builder-summary-title',
			slots: '[data-routine-builder-slots]',
			remove: '[data-routine-builder-remove]',
			total: '[data-routine-builder-total]',
			compare: '[data-routine-builder-compare]',
			saving: '[data-routine-builder-saving]',
			submit: '[data-routine-builder-submit]',
			submitPrice: '[data-routine-builder-submit-price]',
			error: '[data-routine-builder-error]',
			status: '[data-routine-builder-status]',
			dialog: '[data-routine-builder-dialog]',
			dialogClose: '[data-routine-builder-dialog-close]',
			frequency: '[data-routine-builder-frequency]',
			subscribe: '[data-routine-builder-subscribe]',
			once: '[data-routine-builder-once]',
			subSaving: '[data-routine-builder-sub-saving]',
			subCompare: '[data-routine-builder-sub-compare]',
			subTotal: '[data-routine-builder-sub-total]',
			subPercent: '[data-routine-builder-sub-percent]',
			reassurance: '.mm-product-form-buy-button-infos-reassurance-splide',
		},
		classes: {
			filled: 'is-filled',
			visible: 'is-visible',
			hasBar: 'has-bar',
			loading: 'is-loading',
		},
		storageKey: 'mm-routine-builder-',
	},

	instances: new Map(),

	init() {
		document.querySelectorAll(this.config.selectors.root).forEach((root) => this._setup(root));
		if (this.instances.size === 0) return;
		if (!this._eventsBound) {
			this._bindEvents();
			this._eventsBound = true;
		}
	},

	_setup(root) {
		if (this.instances.has(root)) return;

		const state = {
			root,
			tiers: this._parse(root.dataset.tiers, []).sort((a, b) => a.quantity - b.quantity),
			slots: parseInt(root.dataset.slots, 10) || 1,
			subscription: root.dataset.subscription === 'true',
			items: new Map(),
			units: [],
			inView: false,
			formatMoney: this._moneyFormatter(root.dataset.moneySample),
			labels: {
				product: root.dataset.labelProduct || '',
				products: root.dataset.labelProducts || '',
				remove: root.dataset.labelRemove || '',
				removeAria: root.dataset.labelRemoveAria || '',
				saved: root.dataset.labelSaved || '',
				status: root.dataset.labelStatus || '',
				error: root.dataset.labelError || '',
			},
		};

		root.querySelectorAll(this.config.selectors.card).forEach((card) => {
			const item = this._parse(card.dataset.item, null);
			if (!item) return;
			state.items.set(String(item.variantId), { ...item, card });
		});

		state.units = this._restore(state);
		this.instances.set(root, state);
		this._observeSteps(state);
		this._initReassurance(root);
		this._render(state);
	},

	_bindEvents() {
		document.addEventListener('click', (e) => {
			const root = e.target.closest(this.config.selectors.root);
			if (!root) return;
			const state = this.instances.get(root);
			if (!state) return;
			const { selectors } = this.config;

			const add = e.target.closest(selectors.add);
			if (add) {
				this._addUnit(state, add.closest(selectors.card)?.dataset.variantId, true);
				return;
			}
			const increase = e.target.closest(selectors.increase);
			if (increase) {
				this._addUnit(state, increase.closest(selectors.card)?.dataset.variantId, false);
				return;
			}
			const decrease = e.target.closest(selectors.decrease);
			if (decrease) {
				this._removeUnit(state, decrease.closest(selectors.card)?.dataset.variantId);
				return;
			}
			const remove = e.target.closest(selectors.remove);
			if (remove) {
				this._removeUnitAt(state, parseInt(remove.dataset.index, 10));
				return;
			}
			if (e.target.closest(selectors.submit)) {
				this._submit(state);
				return;
			}
			if (e.target.closest(selectors.subscribe)) {
				this._addToCart(state, true);
				return;
			}
			if (e.target.closest(selectors.once)) {
				this._addToCart(state, false);
				return;
			}
			if (e.target.closest(selectors.dialogClose)) {
				root.querySelector(selectors.dialog)?.close();
				return;
			}
			const dialog = e.target.closest(selectors.dialog);
			if (dialog && e.target === dialog) dialog.close();
		});

		document.addEventListener('change', (e) => {
			if (!e.target.matches(this.config.selectors.frequency)) return;
			const state = this.instances.get(e.target.closest(this.config.selectors.root));
			if (state) this._renderDialog(state);
		});

		document.addEventListener('shopify:section:load', () => this.init());
		document.addEventListener('shopify:section:unload', (e) => {
			e.target.querySelectorAll?.(this.config.selectors.root).forEach((root) => {
				this.instances.get(root)?.observer?.disconnect();
				this.instances.delete(root);
			});
		});
	},

	_addUnit(state, variantId, fromAddButton) {
		const item = state.items.get(String(variantId));
		if (!item) return;
		state.units.push(String(variantId));
		this._hideError(state);
		this._render(state);
		if (fromAddButton) item.card.querySelector(this.config.selectors.increase)?.focus();
	},

	_removeUnit(state, variantId) {
		const index = state.units.lastIndexOf(String(variantId));
		if (index === -1) return;
		state.units.splice(index, 1);
		this._render(state);
		const item = state.items.get(String(variantId));
		if (item && !state.units.includes(String(variantId))) {
			item.card.querySelector(this.config.selectors.add)?.focus();
		}
	},

	_removeUnitAt(state, index) {
		if (Number.isNaN(index) || index < 0 || index >= state.units.length) return;
		state.units.splice(index, 1);
		this._render(state);
		const removes = state.root.querySelectorAll(this.config.selectors.remove);
		const visibleRemoves = Array.from(removes).filter((el) => el.offsetParent !== null);
		const next = visibleRemoves.find((el) => parseInt(el.dataset.index, 10) === Math.min(index, state.units.length - 1));
		if (next) {
			next.focus();
		} else {
			const title = state.root.querySelector(this.config.selectors.summaryTitle);
			title?.setAttribute('tabindex', '-1');
			title?.focus();
		}
	},

	_quantities(state) {
		const quantities = new Map();
		state.units.forEach((id) => quantities.set(id, (quantities.get(id) || 0) + 1));
		return quantities;
	},

	_totals(state) {
		const count = state.units.length;
		const percent = state.tiers.reduce((best, tier) => (count >= tier.quantity ? tier.percent : best), 0);
		let compare = 0;
		let discount = 0;
		this._quantities(state).forEach((qty, id) => {
			const line = state.items.get(id).price * qty;
			compare += line;
			discount += Math.round((line * percent) / 100);
		});
		return { count, percent, compare, discount, total: compare - discount };
	},

	_render(state) {
		const { selectors, classes } = this.config;
		const { root } = state;
		const totals = this._totals(state);
		const quantities = this._quantities(state);

		state.items.forEach((item, id) => {
			const qty = quantities.get(id) || 0;
			const add = item.card.querySelector(selectors.add);
			const stepper = item.card.querySelector(selectors.stepper);
			if (!add || !stepper) return;
			add.hidden = qty > 0;
			stepper.hidden = qty === 0;
			const value = item.card.querySelector(selectors.quantity);
			if (value) value.textContent = qty;
		});

		this._renderSlots(state);

		const total = root.querySelector(selectors.total);
		if (total) total.textContent = state.formatMoney(totals.total);
		const compare = root.querySelector(selectors.compare);
		const saving = root.querySelector(selectors.saving);
		const hasDiscount = totals.discount > 0;
		if (compare) {
			compare.hidden = !hasDiscount;
			compare.textContent = hasDiscount ? state.formatMoney(totals.compare) : '';
		}
		if (saving) {
			saving.hidden = !hasDiscount;
			saving.textContent = hasDiscount ? state.labels.saved.replace('[amount]', state.formatMoney(totals.discount)) : '';
		}

		const submit = root.querySelector(selectors.submit);
		if (submit) submit.disabled = totals.count === 0;
		const submitPrice = root.querySelector(selectors.submitPrice);
		if (submitPrice) submitPrice.textContent = totals.count > 0 ? ` — ${state.formatMoney(totals.total)}` : '';

		const status = root.querySelector(selectors.status);
		if (status && state.labels.status) {
			status.textContent = state.labels.status
				.replace('[count]', totals.count)
				.replace('[products]', totals.count === 1 ? state.labels.product : state.labels.products)
				.replace('[total]', state.formatMoney(totals.total));
		}

		root.classList.toggle(classes.hasBar, totals.count > 0);
		this._updateBar(state);
		this._save(state);
	},

	_storageKey(state) {
		return this.config.storageKey + (state.root.dataset.sectionId || '').split('__').pop();
	},

	_restore(state) {
		let saved = [];
		try {
			saved = JSON.parse(window.localStorage.getItem(this._storageKey(state)) || '[]');
		} catch (e) {
			saved = [];
		}
		if (!Array.isArray(saved)) return [];
		return saved
			.map(String)
			.filter((id) => state.items.get(id)?.card.querySelector(this.config.selectors.add));
	},

	_save(state) {
		try {
			if (state.units.length > 0) {
				window.localStorage.setItem(this._storageKey(state), JSON.stringify(state.units));
			} else {
				window.localStorage.removeItem(this._storageKey(state));
			}
		} catch (e) {}
	},

	_renderSlots(state) {
		const list = state.root.querySelector(this.config.selectors.slots);
		if (!list) return;
		const count = Math.max(state.slots, state.units.length);
		const badges = new Map(state.tiers.map((tier) => [tier.quantity, tier.percent]));
		const fragment = document.createDocumentFragment();

		for (let i = 0; i < count; i += 1) {
			const id = state.units[i];
			const item = id ? state.items.get(id) : null;
			const slot = document.createElement('li');
			slot.className = 'mm-routine-builder-slot';
			if (item) slot.classList.add(this.config.classes.filled);

			const thumb = document.createElement('div');
			thumb.className = 'mm-routine-builder-slot-thumb';
			thumb.setAttribute('aria-hidden', 'true');
			if (item?.packshot) thumb.classList.add('is-packshot');
			if (item?.image) {
				const img = document.createElement('img');
				img.src = item.image;
				img.alt = '';
				img.width = 80;
				img.height = 80;
				img.loading = 'lazy';
				thumb.appendChild(img);
			}
			if (badges.has(i + 1)) {
				const badge = document.createElement('span');
				badge.className = 'mm-routine-builder-slot-badge overtitre-lg';
				badge.textContent = `-${badges.get(i + 1)}%`;
				thumb.appendChild(badge);
			}
			slot.appendChild(thumb);

			if (item) {
				const removeLabel = state.labels.removeAria.replace('[product]', item.title);
				const info = document.createElement('div');
				info.className = 'mm-routine-builder-slot-info';
				info.innerHTML = `
					<div class="mm-routine-builder-slot-row overtitre">
						<span class="mm-routine-builder-slot-name"></span>
						<span class="mm-routine-builder-slot-price"></span>
					</div>
					<button type="button" class="mm-routine-builder-slot-link overtitre" data-routine-builder-remove data-index="${i}"></button>`;
				info.querySelector('.mm-routine-builder-slot-name').textContent = item.title;
				info.querySelector('.mm-routine-builder-slot-price').textContent = state.formatMoney(item.price);
				const link = info.querySelector('button');
				link.textContent = state.labels.remove;
				link.setAttribute('aria-label', removeLabel);
				slot.appendChild(info);

				const cross = document.createElement('button');
				cross.type = 'button';
				cross.className = 'mm-routine-builder-slot-remove';
				cross.dataset.routineBuilderRemove = '';
				cross.dataset.index = i;
				cross.setAttribute('aria-label', removeLabel);
				cross.innerHTML = '<svg viewBox="0 0 10 10" width="6" height="6" aria-hidden="true" focusable="false"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" stroke-width="1.4"/></svg>';
				slot.appendChild(cross);
			}
			fragment.appendChild(slot);
		}

		list.replaceChildren(fragment);
	},

	_observeSteps(state) {
		const steps = state.root.querySelector(this.config.selectors.steps);
		if (!steps || !('IntersectionObserver' in window)) {
			state.inView = true;
			return;
		}
		state.observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				state.inView = entry.isIntersecting;
				this._updateBar(state);
			});
		});
		state.observer.observe(steps);
	},

	_updateBar(state) {
		const summary = state.root.querySelector(this.config.selectors.summary);
		summary?.classList.toggle(this.config.classes.visible, state.units.length > 0 && state.inView);
	},

	_initReassurance(root) {
		const el = root.querySelector(this.config.selectors.reassurance);
		if (!el || el.splide || typeof window.Splide === 'undefined') return;
		if (el.querySelectorAll('.splide__slide').length === 0) return;
		el.splide = new window.Splide(el, {
			type: 'loop',
			arrows: false,
			pagination: true,
			perPage: 1,
			drag: 'free',
			snap: true,
			fixedWidth: '100%',
			autoplay: true,
			interval: 4000,
			pauseOnHover: false,
		}).mount();
	},

	_subscribableCount(state) {
		return state.units.filter((id) => state.items.get(id)?.sub).length;
	},

	_submit(state) {
		if (state.units.length === 0) return;
		const dialog = state.root.querySelector(this.config.selectors.dialog);
		if (state.subscription && dialog && typeof dialog.showModal === 'function' && this._subscribableCount(state) > 0) {
			this._renderDialog(state);
			dialog.showModal();
			return;
		}
		this._addToCart(state, false);
	},

	_subscriptionPlan(item, frequency) {
		if (!item?.sub) return null;
		return item.sub.plans[frequency] || item.sub.plans[item.sub.default] || Object.values(item.sub.plans)[0] || null;
	},

	_renderDialog(state) {
		const { selectors } = this.config;
		const dialog = state.root.querySelector(selectors.dialog);
		if (!dialog) return;
		const frequency = dialog.querySelector(selectors.frequency)?.value || '';
		let full = 0;
		let subscribed = 0;
		this._quantities(state).forEach((qty, id) => {
			const item = state.items.get(id);
			const plan = this._subscriptionPlan(item, frequency);
			full += item.price * qty;
			subscribed += (plan ? plan.price : item.price) * qty;
		});
		const saving = Math.max(0, full - subscribed);
		const months = parseInt(frequency, 10);
		const perMonth = /month/.test(frequency) && months > 0 ? Math.round(saving / months) : saving;
		const percent = full > 0 ? Math.round((saving / full) * 100) : 0;

		const compare = dialog.querySelector(selectors.subCompare);
		if (compare) compare.textContent = saving > 0 ? state.formatMoney(full) : '';
		const total = dialog.querySelector(selectors.subTotal);
		if (total) total.textContent = state.formatMoney(subscribed);
		const badge = dialog.querySelector(selectors.subPercent);
		if (badge) badge.textContent = percent > 0 ? `-${percent}%` : '';
		const perMonthEl = dialog.querySelector(selectors.subSaving);
		if (perMonthEl) perMonthEl.textContent = state.formatMoney(perMonth);
	},

	async _addToCart(state, subscribe) {
		const { selectors, classes } = this.config;
		const { root } = state;
		if (state.units.length === 0 || state.pending) return;

		const dialog = root.querySelector(selectors.dialog);
		const frequency = dialog?.querySelector(selectors.frequency)?.value || '';
		const properties = {
			_cart_source: 'routine_sur_mesure',
			_routine_group_id: Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
			_routine_name: root.dataset.cartName || '',
		};
		if (root.dataset.cartImage) properties._routine_product_image_url = root.dataset.cartImage;

		const parentId = parseInt(root.dataset.parentVariant, 10) || null;
		const items = [];
		if (parentId) {
			items.push({
				id: parentId,
				quantity: 1,
				properties: {
					_cart_source: 'routine_sur_mesure_parent',
					_routine_group_id: properties._routine_group_id,
					_routine_name: properties._routine_name,
				},
			});
		}
		this._quantities(state).forEach((quantity, id) => {
			const item = state.items.get(id);
			const plan = subscribe ? this._subscriptionPlan(item, frequency) : null;
			const line = plan
				? { id: item.sub.variantId, quantity, selling_plan: plan.id, properties: { ...properties } }
				: { id: item.variantId, quantity, properties: { ...properties } };
			if (parentId) line.parent_id = parentId;
			items.push(line);
		});

		const buttons = [root.querySelector(selectors.submit), root.querySelector(selectors.subscribe), root.querySelector(selectors.once)].filter(Boolean);
		state.pending = true;
		buttons.forEach((button) => {
			button.disabled = true;
			button.classList.add(classes.loading);
		});
		this._hideError(state);

		let result = null;
		try {
			result = typeof window.mmAddItemsToCart === 'function'
				? await window.mmAddItemsToCart(items)
				: await this._fallbackAdd(items);
		} catch (e) {
			console.error('[MM-Routine] Echec ajout au panier:', e);
		}

		state.pending = false;
		buttons.forEach((button) => {
			button.disabled = false;
			button.classList.remove(classes.loading);
		});

		if (result?.ok) {
			dialog?.open && dialog.close();
			this._render(state);
			document.dispatchEvent(new CustomEvent('global:routine-builder-added', { detail: { items } }));
			return;
		}

		dialog?.open && dialog.close();
		this._showError(state, result?.message);
		this._render(state);
	},

	async _fallbackAdd(items) {
		const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ items }),
		});
		if (res.ok) {
			window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
			return { ok: true };
		}
		const data = await res.json().catch(() => ({}));
		return { ok: false, message: data.description || data.message };
	},

	_showError(state, message) {
		const error = state.root.querySelector(this.config.selectors.error);
		if (!error) return;
		error.textContent = message || state.labels.error;
		error.hidden = false;
	},

	_hideError(state) {
		const error = state.root.querySelector(this.config.selectors.error);
		if (!error) return;
		error.hidden = true;
		error.textContent = '';
	},

	_parse(value, fallback) {
		try {
			return JSON.parse(value);
		} catch (e) {
			return fallback;
		}
	},

	_moneyFormatter(sample) {
		const currency = window.Shopify?.currency?.active;
		if (currency && typeof Intl !== 'undefined') {
			try {
				const format = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency });
				return (cents) => format.format(cents / 100);
			} catch (e) {}
		}
		const text = (sample || '').trim();
		const match = text.match(/1(\D?)234(?:(\D)(\d{2}))?/);
		if (!match) return (cents) => (cents / 100).toFixed(2);
		const [whole, thousands, decimalSeparator, decimals] = match;
		const prefix = text.slice(0, match.index);
		const suffix = text.slice(match.index + whole.length);
		return (cents) => {
			const value = Math.round(cents) / 100;
			const fixed = decimals ? value.toFixed(2) : String(Math.round(value));
			const [integer, fraction] = fixed.split('.');
			const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
			return prefix + grouped + (decimals ? decimalSeparator + fraction : '') + suffix;
		};
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => RoutineBuilderManager.init());
} else {
	RoutineBuilderManager.init();
}
